import {
  TicketsListItemProps,
  TicketsListProps,
} from "@ticketingplatform/ui/dist/components/molecules/TicketsList/types";
import isFuture from "date-fns/isFuture";
import isPast from "date-fns/isPast";
import useDebounceFn from "hooks/useDebounceFn";
import { useOrderContext } from "providers/OrderProvider/hooks";
import { useMemo, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import environment from "setup/environment";
import formatDateTime from "utils/formatDateTime";
import getSaleDate from "utils/getSaleDate";
import parseDate from "utils/parseDate";
import zonedTimeToLocal from "utils/zonedTimeToLocal";
import { useEventOccurrence } from "views/EventDetails/hooks";

import { areFeesIncluded, getPriceToDisplay } from "./utils";

export const useEventTickets = (): Pick<
  TicketsListProps,
  "items" | "onItemsChange" | "disabled"
> & {
  includeAllFees: boolean;
} => {
  const intl = useIntl();
  const { eventOccurrence } = useEventOccurrence(true);
  const { loading: disabled, order, error, setOrder } = useOrderContext();

  const [localQuantities, setLocalQuantities] = useState<
    Record<number, number>
  >({});

  useEffect(() => {
    const initialQuantities = (order?.tickets?.nodes || []).reduce<
      Record<number, number>
    >(
      (prev, { ticketPoolId }) => ({
        ...prev,
        [ticketPoolId]: (prev[ticketPoolId] ?? 0) + 1,
      }),
      {}
    );

    setLocalQuantities(initialQuantities);
  }, [order?.tickets?.nodes]);

  const onItemsChange = useDebounceFn((items: TicketsListItemProps[]) => {
    const newQuantities = items.reduce<Record<number, number>>(
      (prev, { ticketPoolId, value }) =>
        ticketPoolId
          ? {
              ...prev,
              [ticketPoolId]: value,
            }
          : prev,
      {}
    );

    setLocalQuantities(newQuantities);

    setOrder({
      tickets: items.map(({ ticketPoolId, value }) => ({
        ticketPoolId,
        eventOccurrenceId: eventOccurrence?.id,
        quantity: value,
      })),
    });
  }, 1000);

  const includeAllFees = useMemo(
    () => areFeesIncluded(eventOccurrence?.event.venue?.priceDisplay),
    [eventOccurrence?.event.venue?.priceDisplay]
  );

  const items = useMemo<TicketsListItemProps[]>(() => {
    const eventDate = parseDate(eventOccurrence?.date);

    if (!eventDate) {
      return [];
    }

    const { ticketPools, timezone, venue } = eventOccurrence?.event || {};
    const { platformFee } = venue || {};

    return (ticketPools?.nodes || []).map(
      (
        {
          id,
          name,
          description,
          price,
          availableBeforeDays,
          availableBeforeTime,
          expireBeforeDays,
          expireBeforeTime,
          quantity: maxValue,
          priceWithFees,
          isScannerOnly,
        },
        index
      ) => {
        const saleAvailableAt =
          availableBeforeDays !== null &&
          availableBeforeTime !== null &&
          getSaleDate(eventDate, availableBeforeDays, availableBeforeTime);

        const saleExpireAt =
          expireBeforeDays !== null &&
          expireBeforeTime &&
          getSaleDate(eventDate, expireBeforeDays, expireBeforeTime);

        const localSaleAvailableAt =
          saleAvailableAt && zonedTimeToLocal(saleAvailableAt, timezone);

        const localSaleExpireAt =
          saleExpireAt && zonedTimeToLocal(saleExpireAt, timezone);

        const isSaleAvailable =
          (!localSaleAvailableAt || isPast(localSaleAvailableAt)) &&
          (!localSaleExpireAt || isFuture(localSaleExpireAt));

        const displayPrice = getPriceToDisplay(
          { price, priceWithFees },
          venue?.priceDisplay
        );

        const feeLabel = includeAllFees
          ? intl.formatMessage({
              defaultMessage: "(includes fees)",
              description: "includes Fees label",
            })
          : intl.formatMessage({
              defaultMessage: " + Fees",
              description: "Fees label",
            });

        return {
          name,
          ticketPoolId: id,
          key: id,
          error: error?.graphQLErrors.find((graphQLError) =>
            graphQLError.errorInfo?.path?.startsWith(`input.tickets.[${index}]`)
          )?.message,
          price: displayPrice
            ? intl.formatNumber(displayPrice, {
                style: "currency",
                currency: environment.CURRENCY,
              })
            : intl.formatMessage({
                defaultMessage: "Free",
              }),
          feeLabel: price > 0 && platformFee ? feeLabel : undefined,
          dateTime: saleAvailableAt
            ? formatDateTime(saleAvailableAt, timezone)
            : undefined,
          tooltipLabel: description && (
            <div dangerouslySetInnerHTML={{ __html: description }} />
          ),
          disabled: !isSaleAvailable,
          value: localQuantities[id] ?? 0,
          maxValue,
          isScannerOnly,
        };
      }
    );
  }, [
    error?.graphQLErrors,
    eventOccurrence?.date,
    eventOccurrence?.event,
    includeAllFees,
    intl,
    localQuantities,
  ]);

  return {
    items,
    onItemsChange,
    disabled,
    includeAllFees: areFeesIncluded(eventOccurrence?.event.venue?.priceDisplay),
  };
};
